/* Drawing the box: the slabs behind and beneath the surface, and the crosshair that locates a
   hovered point against them.

   Chrome, not data. Every face is a step of grey off the chart background, ruled with the same
   hairline grid `renderAxes3d` uses on the floor and edged at full weight - enough to read the box
   as a solid, not enough to compete with a relief map drawn on top of it.

   What makes it read as a solid rather than as three panels is that the faces are shaded apart. A
   wall's inner face is almost the background; the faces of its thickness are a clear step darker.
   The eye takes the step as a change of orientation, which is the only cue a flat drawing has. */

import type { CameraState, Size } from '../types';
import {
  clampPitch,
  createProjector,
  getBackCorner,
  getCameraBasis,
} from '../geometry/projection';
import {
  AXIS_BOX_THICKNESS,
  axisBoxFaces,
  edgesOf,
  axisBoxGrid,
  crosshairSegments,
  type AxisBoxFace,
  type AxisBoxFaceKind,
  type UnitPoint,
  type UnitSegment,
} from './axisBox';
import { GRID_INK_TONE, GRID_WIDTH } from './gridStroke';
import {
  CORNER_REACH,
  PLANE_MAX_INK,
  parseColor,
  toCornerStops,
  toPanelGradient,
} from './panelShading';
import type { Rgb } from './colorScale';
import type { BackCorner, CameraBasis } from '../geometry/projection';
import type { ProjectedPoint } from '../types';
import type { AxisInset } from '../data/horizontalAxes';

/** The faces the data is read against, which are lit rather than washed. See `panelShading`. */
const isPlane = (kind: AxisBoxFaceKind): boolean =>
  kind === 'floor' || kind === 'wall';

/**
 * The depth of the nearest and furthest corner of the whole box.
 *
 * Scene-wide rather than per face, because the depth wash is a statement about where a point sits
 * in the box. Normalised per face, a wall standing at the back would sweep the same full ramp the
 * floor does and the two would disagree about where "far" is at the edge they share.
 */
const depthRangeOf = (
  faces: AxisBoxFace[],
  project: (x: number, y: number, z: number) => { depth: number }
): [min: number, max: number] => {
  let min = Infinity;
  let max = -Infinity;

  for (const face of faces) {
    for (const [x, y, z] of face.points) {
      const { depth } = project(x, y, z);
      if (depth < min) min = depth;
      if (depth > max) max = depth;
    }
  }

  return [min, max];
};

/**
 * A linear gradient along the face's own depth axis, or null if it has none to speak of.
 *
 * Anchored to the projected corners rather than to a screen direction: the face is a quad in the
 * unit cube, so its furthest and nearest corners are where its depth range actually ends, and the
 * gradient between them follows the geometry at any yaw. A face square to the eye has both corners
 * at one depth, and `createLinearGradient` between two coincident points paints nothing.
 */
const panelGradientFor = (
  context: CanvasRenderingContext2D,
  face: AxisBoxFace,
  project: (
    x: number,
    y: number,
    z: number
  ) => { screenX: number; screenY: number; depth: number },
  [boxMin, boxMax]: [number, number],
  panel: Rgb,
  ink: Rgb,
  basis: CameraBasis
): CanvasGradient | null => {
  const projected = face.points.map(([x, y, z]) => project(x, y, z));
  const near = projected.reduce((best, point) =>
    point.depth > best.depth ? point : best
  );
  const far = projected.reduce((best, point) =>
    point.depth < best.depth ? point : best
  );

  if (Math.hypot(near.screenX - far.screenX, near.screenY - far.screenY) < 1) {
    return null;
  }

  const span = boxMax - boxMin || 1;
  const gradient = context.createLinearGradient(
    far.screenX,
    far.screenY,
    near.screenX,
    near.screenY
  );

  for (const { offset, color } of toPanelGradient(
    panel,
    ink,
    face.normal,
    basis,
    [(far.depth - boxMin) / span, (near.depth - boxMin) / span]
  )) {
    gradient.addColorStop(offset, color);
  }

  return gradient;
};

/**
 * The shadow the box's inside corner casts on one of its planes.
 *
 * Centred on the projected corner where the two walls meet the floor - the real one, so it follows
 * the yaw with the rest of the box - and reaching `CORNER_REACH` of the furthest any point of the
 * box gets from that corner on screen, which tracks the zoom and cannot collapse at any camera. A
 * radial gradient rather than a ramp along the face, because occlusion falls off from a point: it
 * is darkest where three surfaces meet and fades in every direction from there, which no gradient
 * along a single axis can say.
 *
 * The corner is at `z = 0` on both back edges, which is a point every visible plane touches or
 * faces, so one gradient serves all three and they agree along the creases they share.
 */
const cornerShadowFor = (
  context: CanvasRenderingContext2D,
  corner: BackCorner,
  faces: AxisBoxFace[],
  project: (x: number, y: number, z: number) => ProjectedPoint,
  ink: Rgb
): CanvasGradient => {
  const at = project(corner[0], corner[1], 0);
  const span = Math.max(
    ...faces.flatMap(face =>
      face.points.map(([x, y, z]) => {
        const point = project(x, y, z);
        return Math.hypot(
          point.screenX - at.screenX,
          point.screenY - at.screenY
        );
      })
    )
  );
  const reach = span * CORNER_REACH;

  const gradient = context.createRadialGradient(
    at.screenX,
    at.screenY,
    0,
    at.screenX,
    at.screenY,
    reach
  );

  for (const { offset, color } of toCornerStops(ink)) {
    gradient.addColorStop(offset, color);
  }

  return gradient;
};

/**
 * Where the light comes from, given where the eye is.
 *
 * Camera-relative for stability rather than for correctness: what stops the two walls collapsing to
 * one tone is the half-Lambert below, and a light fixed in the unit cube separates them too. What a
 * fixed light does instead is sweep across the box as it orbits, so a wall changes tone while the
 * data under it does not. Tied to the eye, the lit side stays where the viewer left it and the box
 * reads the same at every angle.
 *
 * Above and to one side, though only the sideways part of that is doing anything now. Every face
 * still shaded from this light is a sideways one - the horizontal two have anchors of their own -
 * so the vertical term drops out of the dot product entirely: the `0.6` sets how much of the ramp a
 * quarter turn sweeps, and the `0.8` is only what makes the pair a unit vector.
 *
 * It used to be what kept the horizontal faces at the ends of the ramp, and it did that too
 * cheaply: being constant across every face, it moved the whole ramp rather than separating
 * anything, and both horizontal faces ended up inside the readability floor of a sideways face they
 * meet. `TONE.rimTop` and `TONE.rimUnder` state those two readings directly instead.
 */
const lightFor = (camera: CameraState): UnitPoint => {
  const { right } = getCameraBasis(camera.yaw, clampPitch(camera.pitch));
  const light: UnitPoint = [right[0] * 0.6, right[1] * 0.6, 0.8];
  const length = Math.hypot(...light);
  return [light[0] / length, light[1] / length, light[2] / length];
};

/**
 * How much `inkColor` a face carries over `panelColor`.
 *
 * A scale of *distinctness from the panel*, not of luminance. Ink is whatever contrasts with the
 * surface, so more of it is darker in the light theme and lighter in the dark one - the ramp holds
 * either way, but which face looks lit follows the theme. Conventional shading would want the
 * horizontal faces lightest in both, which needs a highlight and a shadow colour rather than one
 * ink.
 *
 * The floor and the inner faces take the flat wash: they are the surfaces the data is read against,
 * and shading them would put a gradient behind the plot. Everything else is the box's thickness,
 * and the *sideways* faces are shaded by which way they face rather than by whether they are
 * horizontal or vertical - the two wall ends have the same orientation and opposite normals, so a
 * table keyed on orientation gave them the same tone and the corner between them disappeared.
 *
 * The two horizontal cases are still keyed on orientation, and that is not the same mistake: there
 * is exactly one up-facing tone's worth of face on the box and one down-facing, so neither has a
 * pair for a shared tone to flatten. What `rimTop` and `rimUnder` say about that is below.
 *
 * Shaded with ink rather than with `gridColor`. Grid grey is only about thirty steps off the panel,
 * so buying a visible difference costs most of the alpha range and leaves nothing to separate one
 * rim from another; ink is two hundred steps off.
 */
/**
 * What every thickness face carries before its own tone, so the planes have room below it.
 *
 * The planes are lit now, and a lit plane needs a span of ink to be lit *in* - `PLANE_MAX_INK` of
 * it at the worst-lit corner of the furthest wall. The thickness faces used to start at 0.055,
 * which left the planes 0.035 to work with: seven value steps on the light theme, which is a
 * breath rather than a shading.
 *
 * Added to every face below equally, which is the point. The tones in this table were measured
 * against each other over several rounds and `renderAxisBox.test.ts` holds every pair that meets to
 * a floor; a constant offset moves the whole ramp and changes no difference within it. What it does
 * change is the gap from the ramp down to the planes, and that is the gap being bought.
 */
const RIM_SHIFT = PLANE_MAX_INK + 0.03;

export const TONE = {
  panel: 0.03,
  /**
   * Facing up.
   *
   * Its own anchor rather than a point on the ramp below. The light's z term is the same for every
   * face in the box - nothing about a top rim changes with the camera's yaw - so all it was doing
   * on the ramp was putting the top lightest, and it was paying for that out of the sideways budget:
   * the top rim landed 0.012 from the lit vertical at the shipped camera and 0.0076 at the worst
   * yaw, under the floor `renderAxisBox.test.ts` holds every other adjacent pair to. Anchoring it
   * here separates all three visible thickness faces without narrowing the two verticals.
   *
   * Up-facing rather than horizontal, because down-facing has an anchor of its own below. Putting
   * both on one tone would flatten nothing today - they are never visible together - but they are
   * opposite ends of the ramp, not one value.
   */
  rimTop: 0.055,
  /** Facing the light. */
  rimLit: 0.065,
  /** Facing away from it. */
  rimShaded: 0.15,
  /**
   * Facing down: the plinth's underside, which shows once the camera drops below the plane.
   *
   * The same argument as `rimTop` and the same mistake before it. Leaving it on the ramp put it at
   * `0.1415`, a constant, while the shaded plinth side it shares an edge with runs
   * `0.1075 + 0.0255·sin(yaw)` - between 0.0086 and 0.019 apart for every below-plane camera,
   * where the flat three-tone version had that pair a clear 0.045. Widening the sideways budget to
   * separate the top rim is what pushed them together, so this is the other end of the same trade.
   *
   * Darker than `rimShaded`, so the underside is the darkest face on the box, and one face means no
   * pair for a shared tone to flatten.
   */
  rimUnder: 0.17,
} as const;

/* The grid's stroke lives in `gridStroke`, shared with the floor grid in `renderAxes3d` so the two
   cannot drift apart again. */
/**
 * The edges between faces.
 *
 * Thin and dark rather than thick and grey. Every face already differs in tone from its neighbour,
 * so the edge only has to be crisp - and one of these edges is the mitre at the corner, which is
 * the line that says the walls have thickness.
 */
const EDGE_WIDTH = 1;
const EDGE_ALPHA = 0.45;
const CROSSHAIR_ALPHA = 1;
/**
 * How square-on a face has to be to count as facing the eye.
 *
 * Not zero. A wall turned exactly edge-on gives a dot product of about 1e-16, whose sign falls out
 * of floating point rather than out of the geometry - so whether a zero-width sliver was drawn
 * depended on which corner the camera happened to be at. The sliver is invisible either way; the
 * point of the epsilon is that the answer is the same every time.
 */
const FACING_EPSILON = 1e-6;

type Project = (
  x: number,
  y: number,
  z: number
) => {
  screenX: number;
  screenY: number;
  depth: number;
};

const dot = (
  [ax, ay, az]: UnitPoint,
  [bx, by, bz]: readonly [number, number, number]
): number => ax * bx + ay * by + az * bz;

/** How square-on to the vertical a face has to point to count as facing up or down. */
const VERTICAL_EPSILON = 1e-6;

const toneOf = ({ kind, normal }: AxisBoxFace, light: UnitPoint): number => {
  /* Only reached for a plane when the panel or the ink could not be parsed; the lit path in
     `renderAxisBox` takes them otherwise, and takes no shift because there is no ramp above it to
     stay clear of. */
  if (kind === 'floor' || kind === 'wall') return TONE.panel;

  /* The two faces the ramp cannot place, each anchored instead. There is exactly one of each on the
     box - every wall top faces the same way, and the underside is a single quad - so a fixed tone
     has no pair of them to flatten, which is what a table keyed on orientation did to the wall
     ends. */
  if (normal[2] > 1 - VERTICAL_EPSILON) return RIM_SHIFT + TONE.rimTop;
  if (normal[2] < -1 + VERTICAL_EPSILON) return RIM_SHIFT + TONE.rimUnder;

  /* Half-Lambert rather than clamped Lambert: `dot` mapped from [-1, 1] onto [0, 1] instead of
     discarding everything below zero. A rim facing away from the light is still a face the eye can
     see - the box is open, not lit from inside - and clamping put every one of them on the same
     shaded end, which is what a table keyed on orientation already did.

     Every face that reaches here is sideways, so the light's z term contributes nothing to the dot
     product: what the `0.6` in `lightFor` sets is how much of the ramp a quarter turn sweeps, and
     the `0.8` is only what makes the pair a unit vector. */
  const lit = (dot(normal, light) + 1) / 2;
  return RIM_SHIFT + TONE.rimShaded - (TONE.rimShaded - TONE.rimLit) * lit;
};

const tracePolygon = (
  context: CanvasRenderingContext2D,
  points: UnitPoint[],
  project: Project
): void => {
  context.beginPath();
  points.forEach(([x, y, z], index) => {
    const { screenX, screenY } = project(x, y, z);
    if (index === 0) {
      context.moveTo(screenX, screenY);
      return;
    }
    context.lineTo(screenX, screenY);
  });
  context.closePath();
};

const traceSegments = (
  context: CanvasRenderingContext2D,
  segments: UnitSegment[],
  project: Project
): void => {
  segments.forEach(([from, to]) => {
    const start = project(...from);
    const end = project(...to);
    context.moveTo(start.screenX, start.screenY);
    context.lineTo(end.screenX, end.screenY);
  });
};

const meanDepth = (points: UnitPoint[], project: Project): number =>
  points.reduce((total, point) => total + project(...point).depth, 0) /
  points.length;

/**
 * The faces the eye can see, furthest first.
 *
 * A face is turned away when its outward normal points away from `forward`, which is the camera
 * basis vector running from the plot toward the eye. Testing the normal rather than the projected
 * winding keeps the rule in the space the geometry was defined in - and the normals are exact,
 * because every face is axis-aligned.
 *
 * Nothing is exempt. The floor is the top of a plinth rather than a bare plane, so the solid has an
 * underside of its own to show when the camera drops below it - which is what lets this be one rule
 * for every face instead of a rule and an exception.
 *
 * Sorted so a nearer face paints over a further one. The wall slabs make an L in plan and so are
 * not one convex solid, which is enough for a low camera to project one arm's rim across the
 * other's inner face.
 */
/* Exported for its own tests: which faces the eye can see is the rule that decides whether the box
   reads as a solid, and it is not observable from the finished drawing. */
export const visibleFaces = (
  faces: AxisBoxFace[],
  camera: CameraState,
  project: Project
): AxisBoxFace[] => {
  const { forward } = getCameraBasis(camera.yaw, clampPitch(camera.pitch));

  return faces
    .filter(face => dot(face.normal, forward) > FACING_EPSILON)
    .map(face => ({ face, depth: meanDepth(face.points, project) }))
    .sort((a, b) => a.depth - b.depth)
    .map(({ face }) => face);
};

export type AxisBoxRenderArgs = {
  context: CanvasRenderingContext2D;
  camera: CameraState;
  viewport: Size;
  /**
   * The text color. Every face is a fraction of it, and so are the edges and the grid.
   */
  inkColor: string;
  /** The box's own surface. Each face is filled with it before its tone, so faces cannot stack. */
  panelColor: string;
  /**
   * Divisions of each axis, which the walls' uprights and height rules follow.
   *
   * The same three counts `renderAxes3d` is given: the uprights continue the floor's rules up the
   * box, so a count that disagrees with the floor's breaks the line at the crease.
   */
  xTickCount: number;
  yTickCount: number;
  zTickCount: number;
  /** The fraction of each horizontal axis its padding takes at each end, which the uprights skip. */
  xTickInset: AxisInset;
  yTickInset: AxisInset;
  /**
   * Which part of the box to draw. The four go at four points in the frame, because each has
   * different things that must be in front of it and behind it.
   *
   * `'planes'` - the floor and the walls' inner faces. The planes the grid and the vertical axis are
   * ruled *on*, so they go first, on the plane layer, and the axes land on top of them.
   *
   * `'wallThickness'` - the walls' depth. Still the plane layer, but *after* the axes: these faces
   * stand in front of the floor plane, so a grid line drawn over one shows through it. It cannot go
   * later than that, because a wall's top rim is at full height and the surface can rise in front of
   * it.
   *
   * `'plinthBase'` - the plinth, on the plane layer, before the mesh. Everything that would otherwise
   * be painted across the data goes here.
   *
   * `'plinthThickness'` - the plinth, on the chrome layer, after the surface, where drop lines run to
   * the floor plane on that layer and the plinth stands in front of them.
   *
   * Which of the last two a plinth face lands in is not fixed: `passOf` decides it from the camera,
   * and the reasoning is there rather than repeated here.
   */
  pass: AxisBoxPass;
};

type AxisBoxPass =
  | 'planes'
  | 'wallThickness'
  | 'plinthBase'
  | 'plinthThickness';

/** Order the passes are drawn in, which is what decides who has already stroked a shared edge. */
const PASS_ORDER: AxisBoxPass[] = [
  'planes',
  'wallThickness',
  'plinthBase',
  'plinthThickness',
];

/** Whether the camera has gone under the plane the data is read against. */
const isBelowPlane = (camera: CameraState): boolean =>
  getCameraBasis(camera.yaw, clampPitch(camera.pitch)).forward[2] < 0;

/**
 * Which pass a face is drawn in, and for the plinth that depends on where the camera is.
 *
 * The plinth is the only part of the box drawn after the mesh, and it is there for one reason:
 * drop lines run down to the floor plane on the surface's own layer, and the plinth stands in
 * front of them. That reason exists only while the camera is above the plane - and so does the
 * safety of it. Below the plane every plinth face projects across the footprint rather than along
 * its edge: the underside as a quad over the whole plot, and each side as a band that sweeps in
 * from the middle toward the front edge as the camera drops. Both would be painted opaquely over
 * the surface, the wireframe and the isolines.
 *
 * So below the plane the whole plinth is drawn before the mesh, where nothing it covers is data.
 * The sign flips exactly at the plane, which is why the shipped camera never showed either.
 */
const passOf = (kind: AxisBoxFaceKind, camera: CameraState): AxisBoxPass => {
  if (kind === 'wallRim') return 'wallThickness';
  if (kind === 'plinthBase') return 'plinthBase';
  if (kind === 'plinthRim') {
    return isBelowPlane(camera) ? 'plinthBase' : 'plinthThickness';
  }
  return 'planes';
};

/** Direction-free, so an edge two faces wind opposite ways is recognised as the one edge. */
const segmentKey = ([from, to]: UnitSegment): string =>
  [from.join(), to.join()].sort().join('|');

const subtract = (
  [ax, ay, az]: UnitPoint,
  [bx, by, bz]: UnitPoint
): UnitPoint => [ax - bx, ay - by, az - bz];

const cross = ([ax, ay, az]: UnitPoint, [bx, by, bz]: UnitPoint): UnitPoint => [
  ay * bz - az * by,
  az * bx - ax * bz,
  ax * by - ay * bx,
];

/** Slack for the arithmetic, in unit-cube units: well under the 0.05 a slab is thick. */
const COLLINEAR_EPSILON = 1e-9;
const EXTENT_EPSILON = 1e-6;

/**
 * Whether a fill along `edge` would paint over `segment`.
 *
 * Containment rather than equality, which is the point: the plinth's front side runs the full width
 * of the slab, out to the wall's outer face, while the floor's front edge stops at the data
 * footprint. The two are collinear and one contains the other, so matching by identity finds nothing
 * while the fill covers the stroke along its whole length.
 */
const edgeCovers = (edge: UnitSegment, segment: UnitSegment): boolean => {
  const [from, to] = edge;
  const along = subtract(to, from);
  const length = dot(along, along);
  if (length === 0) return false;

  return segment.every(point => {
    const offset = subtract(point, from);
    const perpendicular = cross(offset, along);
    if (dot(perpendicular, perpendicular) > COLLINEAR_EPSILON * length) {
      return false;
    }
    const position = dot(offset, along) / length;
    return position > -EXTENT_EPSILON && position < 1 + EXTENT_EPSILON;
  });
};

/**
 * Which pass strokes each edge, so that every edge is stroked exactly once and by nobody too early.
 *
 * Two invariants, and the second is why this is not "skip what an earlier pass drew". Every pass
 * fills before it strokes and the fill is opaque, so an edge stroked in one pass and lying on the
 * boundary of a face filled in a later one loses the half of its width on that face's side. And a
 * segment in two `stroke` calls on one layer composites twice.
 *
 * So each edge goes to the latest visible pass that touches it, where touching counts a face's own
 * polygon and not only the outline it chose to draw - the plinth's sides drop the floor's front
 * edges from their outlines while still filling across them, so the polygon is what claims those.
 *
 * A fixed owner per face is wrong: neither side is reliably the visible one. A wall seen edge-on
 * from overhead is culled while its rim is not, and the top rim is culled below level while the wall
 * is not, so a fixed owner loses the edge in whichever case that owner went away.
 */
const partitionStrokes = (
  faces: AxisBoxFace[],
  camera: CameraState,
  project: Project
): Record<AxisBoxPass, UnitSegment[]> => {
  const visible = visibleFaces(faces, camera, project);

  /* Only what some visible face asked to be drawn. A polygon edge claims an edge that is already
     wanted; it never adds one, or the creases every slab is made of would all become lines. */
  const wanted = new Map<string, UnitSegment>();
  const outlineKeys = visible.map(face => {
    const keys = new Set<string>();
    face.outline.forEach(segment => {
      const key = segmentKey(segment);
      keys.add(key);
      wanted.set(key, segment);
    });
    return keys;
  });

  const owner = new Map<string, number>();
  visible.forEach((face, index) => {
    const pass = PASS_ORDER.indexOf(passOf(face.kind, camera));
    const keys = outlineKeys[index];
    const fillEdges = edgesOf(face.points);

    wanted.forEach((segment, key) => {
      const touches =
        keys.has(key) || fillEdges.some(edge => edgeCovers(edge, segment));
      if (!touches) return;
      owner.set(key, Math.max(owner.get(key) ?? -1, pass));
    });
  });

  const partition = Object.fromEntries(
    PASS_ORDER.map(pass => [pass, [] as UnitSegment[]])
  ) as Record<AxisBoxPass, UnitSegment[]>;

  wanted.forEach((segment, key) => {
    const pass = PASS_ORDER[owner.get(key) ?? -1];
    if (pass) partition[pass].push(segment);
  });

  return partition;
};

/**
 * The last partition computed, reused across the passes of one frame.
 *
 * A frame calls `renderAxisBox` once per pass with everything but the pass identical, and the
 * partition is a pure function of the rest - so computing it per call did the same work four times.
 * Measured at 1.85 ms for the calls of one frame, effectively all of it here, against a 16 ms budget
 * that an orbit spends most of on the mesh.
 *
 * One entry rather than a map: the only repetition worth catching is within a frame, and a camera
 * that has moved has invalidated it anyway.
 */
let cachedPartition: {
  key: string;
  partition: Record<AxisBoxPass, UnitSegment[]>;
} | null = null;

const strokesByPass = (
  faces: AxisBoxFace[],
  camera: CameraState,
  viewport: Size,
  project: Project
): Record<AxisBoxPass, UnitSegment[]> => {
  const key = [
    camera.yaw,
    camera.pitch,
    camera.zoom,
    camera.perspective,
    camera.heightScale,
    viewport.width,
    viewport.height,
  ].join('|');

  if (cachedPartition?.key === key) return cachedPartition.partition;

  const partition = partitionStrokes(faces, camera, project);
  cachedPartition = { key, partition };
  return partition;
};

/**
 * One pass of the box, filled and then outlined.
 *
 * Called once per pass with everything else identical, because the box straddles what the axes and
 * the mesh are drawn on: its planes go under both, its wall thickness over the axes and under the
 * mesh, and its plinth over the mesh except for the one face that would cover it. Which pass a face
 * belongs to is `passOf`; where each pass is drawn is the controller's.
 */
export const renderAxisBox = ({
  context,
  camera,
  viewport,
  inkColor,
  panelColor,
  xTickCount,
  yTickCount,
  zTickCount,
  xTickInset,
  yTickInset,
  pass,
}: AxisBoxRenderArgs): void => {
  const project = createProjector(camera, viewport);
  const corner = getBackCorner(camera.yaw);
  const box = axisBoxFaces(corner, AXIS_BOX_THICKNESS, camera.heightScale);
  const faces = visibleFaces(box, camera, project).filter(
    face => passOf(face.kind, camera) === pass
  );
  const owned = strokesByPass(box, camera, viewport, project)[pass];

  const light = lightFor(camera);

  /* The planes are lit as colours rather than as ink over the panel, which needs both ends of the
     blend as numbers. `null` for anything the token system resolves to that `parseColor` does not
     read, and then every face takes the flat path below - a plane a step off the panel, which is
     what shipped before this. A wrong colour would be worse than an unlit one. */
  const panel = parseColor(panelColor);
  const ink = parseColor(inkColor);
  const basis = getCameraBasis(camera.yaw, clampPitch(camera.pitch));
  const depthRange = depthRangeOf(box, project);

  context.save();
  context.lineJoin = 'round';

  /* Two paths, because the planes and the box's thickness answer different questions. A plane is
     lit: it carries one tone from its own normal and a wash toward ink with depth, filled once as
     an opaque gradient - one fill, so a seam cannot double it. A rim face keeps the flat pair, the
     panel colour first so a face cannot show what is behind it and its tone over that, which is
     what `TONE` was measured against. */
  /* Over every visible face, not only the lit planes, and that is the fix for a real inversion
     rather than a tidy-up. Occlusion is a property of the scene: light reaches a crease least
     whatever surface happens to be there, so the wall's own top rim is as shadowed near the corner
     as the wall is. Applied to the planes alone it darkened only one side of every pair that meets
     there - measured, the wall's inner face reached 0.513 of ink at the corner against its top
     rim's 0.345, so the plane was 0.168 *darker* than the thickness it is supposed to sit inside
     and the box read inside-out along that edge.

     It also makes the clearance `RIM_SHIFT` buys hold by construction instead of by arithmetic:
     the gradient is in screen space, so two faces meeting at a point take the same term there and
     every difference between them is left exactly as `TONE` measured it. */
  const cornerShadow = ink
    ? cornerShadowFor(context, corner, box, project, ink)
    : null;

  faces.forEach(face => {
    tracePolygon(context, face.points, project);

    const gradient =
      panel && ink && isPlane(face.kind)
        ? panelGradientFor(
            context,
            face,
            project,
            depthRange,
            panel,
            ink,
            basis
          )
        : null;

    /* The corner shadow goes on last either way, over whatever the face was filled with, on the
       same path. Transparent, so a seam between two faces can double it - one pixel of an already
       soft shadow, where the alternative is clipping to their union and tracing every face
       twice. */
    const shadeTheCorner = () => {
      if (!cornerShadow) return;
      context.globalAlpha = 1;
      context.fillStyle = cornerShadow;
      context.fill();
    };

    if (gradient) {
      context.globalAlpha = 1;
      context.fillStyle = gradient;
      context.fill();
      shadeTheCorner();
      return;
    }

    const tone = toneOf(face, light);
    context.globalAlpha = 1;
    context.fillStyle = panelColor;
    context.fill();
    context.globalAlpha = tone;
    context.fillStyle = inkColor;
    context.fill();
    shadeTheCorner();
  });

  /* Ruled in the `planes` pass, on the planes it belongs to. Drawing it in a later one would put
     wall grid over the thickness faces, which is the same transparency by another route. */
  if (pass === 'planes') {
    context.strokeStyle = inkColor;
    context.lineWidth = GRID_WIDTH;
    context.globalAlpha = GRID_INK_TONE;
    context.beginPath();
    traceSegments(
      context,
      axisBoxGrid(corner, xTickCount, yTickCount, zTickCount, {
        x: xTickInset,
        y: yTickInset,
      }),
      project
    );
    context.stroke();
  }

  /* One path for every edge, stroked once. Stroking face by face would lay two strokes wherever two
     faces share an edge, and at this alpha that reads as some edges being darker than others for no
     reason a viewer could name. */
  context.strokeStyle = inkColor;
  context.lineWidth = EDGE_WIDTH;
  context.globalAlpha = EDGE_ALPHA;
  context.beginPath();
  traceSegments(context, owned, project);
  context.stroke();

  context.restore();
};

export type Crosshair3dRenderArgs = {
  context: CanvasRenderingContext2D;
  camera: CameraState;
  viewport: Size;
  /** Where the hovered point sits in the unit cube. */
  point: UnitPoint;
  inkColor: string;
  surfaceColor: string;
};

/**
 * The three axis-parallel lines through the hovered point.
 *
 * Drawn in the chrome pass, over the surface: the whole job is to be followed from a peak out to
 * the walls, and a line that disappears behind the relief it crosses cannot be. Haloed for the
 * same reason the tick labels are — the surface it crosses is any color the ramp chooses, and ink
 * on a dark band of the ramp is ink on ink.
 */
export const renderCrosshair3d = ({
  context,
  camera,
  viewport,
  point,
  inkColor,
  surfaceColor,
}: Crosshair3dRenderArgs): void => {
  const project = createProjector(camera, viewport);
  const segments = crosshairSegments(point);

  context.save();
  context.lineCap = 'round';

  context.strokeStyle = surfaceColor;
  context.lineWidth = 3;
  context.globalAlpha = 0.5;
  context.beginPath();
  traceSegments(context, segments, project);
  context.stroke();

  context.strokeStyle = inkColor;
  context.lineWidth = 1;
  context.globalAlpha = CROSSHAIR_ALPHA;
  context.beginPath();
  traceSegments(context, segments, project);
  context.stroke();

  context.restore();
};
